import { SlidersHorizontal, X } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useIsDesktop } from '@/shared/hooks/use-is-desktop';
import { useTranslation } from '@/shared/i18n/use-translation';
import { Badge } from '@/shared/ui/badge/badge';
import { Button } from '@/shared/ui/button/button';
import { Sheet } from '@/shared/ui/sheet/sheet';
import { SheetContent } from '@/shared/ui/sheet/sheet-content';
import { SheetFooter } from '@/shared/ui/sheet/sheet-footer';
import { SheetHeader } from '@/shared/ui/sheet/sheet-header';
import { SheetTitle } from '@/shared/ui/sheet/sheet-title';

type FiltersBarProps = {
  /** The filter controls (`FilterSelect`s). */
  readonly children: ReactNode;
  /** How many filters differ from their default. */
  readonly active: number;
  readonly onClear: () => void;
};

/**
 * Desktop: the filters in a row, with "Clear filters" when any is set. Phones: a "Filters (n)"
 * button opening a bottom sheet, so the row never overflows the screen.
 */
export function FiltersBar({ children, active, onClear }: FiltersBarProps) {
  const { t } = useTranslation();
  const desktop = useIsDesktop();
  const [open, setOpen] = useState(false);
  const clear =
    active > 0 ? (
      <Button variant="ghost" size="lg" onClick={onClear}>
        <X /> {t('Clear filters')}
      </Button>
    ) : null;

  if (desktop) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        {children}
        {clear}
      </div>
    );
  }
  return (
    <>
      <Button
        variant="outline"
        size="icon"
        className="relative size-10 shrink-0"
        aria-label={active > 0 ? t('Filters ({count} on)', { count: active }) : t('Filters')}
        onClick={() => setOpen(true)}
      >
        <SlidersHorizontal />
        {active > 0 && (
          <Badge className="absolute -top-1.5 -right-1.5 h-5 min-w-5 rounded-full px-1 tabular-nums">
            {active}
          </Badge>
        )}
      </Button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="max-h-[85dvh] gap-0 rounded-t-2xl p-0">
          <SheetHeader className="border-b px-5 pt-5 pb-4">
            <SheetTitle className="text-lg">{t('Filters')}</SheetTitle>
          </SheetHeader>
          <div data-slot="filters-sheet" className="flex flex-col gap-4 overflow-y-auto px-5 py-5">
            {children}
          </div>
          <SheetFooter className="flex-row justify-end gap-2 border-t px-5 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
            {clear}
            <Button size="lg" onClick={() => setOpen(false)}>
              {t('Show results')}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
}
